import { FC, RefObject, useRef, useMemo, useLayoutEffect } from 'react';
import Loader from '__components/Kizen/Loader';
import { MessageType } from './index';
import { Message } from './Message';
import { PreviewCards } from './PreviewCards';
import { useTranslation } from 'react-i18next';
import type { PreviewItem } from 'ts-components/ChatBotV2/types';

interface MessageHistoryProps {
  messageHistoryWrapperRef: RefObject<HTMLDivElement>;
  isLoadingNewSession: boolean;
  isMessageResponseLoading: boolean;
  messages: MessageType[];
  lastUserMessageRef: RefObject<HTMLDivElement>;
  lastAssistantMessageRef: RefObject<HTMLDivElement>;
  shrinkHeightAmount?: number;
  scrollBottomRef?: RefObject<HTMLDivElement>;
  scrollToHeight: number;
  showFixedHeight?: boolean;
  hasUserSentAMessage?: boolean;
  hideRefresh?: boolean;
  renderMarkdown?: boolean;
  hideLoadingSpinner?: boolean;
  disableMessageAnimation?: boolean;
  onCopyMessage?: (messageContent: string) => void;
  onLikeMessage?: () => void;
  onDislikeMessage?: () => void;
  onRefreshMessage?: () => void;
  onPreviewSelect?: (preview: PreviewItem) => void;
  previewSpacerRef?: RefObject<HTMLDivElement>;
  hideScrollbar?: boolean;
}

export const MessageHistory: FC<MessageHistoryProps> = ({
  messageHistoryWrapperRef,
  isLoadingNewSession,
  isMessageResponseLoading,
  messages,
  lastUserMessageRef,
  lastAssistantMessageRef,
  scrollBottomRef,
  scrollToHeight,
  showFixedHeight,
  hasUserSentAMessage,
  hideRefresh,
  renderMarkdown,
  hideLoadingSpinner = false,
  disableMessageAnimation = false,
  onCopyMessage,
  onLikeMessage,
  onDislikeMessage,
  onRefreshMessage,
  onPreviewSelect,
  previewSpacerRef,
  hideScrollbar = false,
}) => {
  const { t } = useTranslation();
  const animatingMessages = useRef<string[]>([]);
  const hasAnimatedMessages = useRef<Set<string>>(
    new Set(messages.map((x) => x.id))
  );

  useLayoutEffect(() => {
    // Only animate messages that appear after the user sent messages, not old messages from hydration
    messages.forEach((msg) => {
      if (!hasAnimatedMessages.current.has(msg.id)) {
        hasAnimatedMessages.current.add(msg.id);
        if (hasUserSentAMessage && messages.length > 1) {
          animatingMessages.current.push(msg.id);
        }
      }
    });
  }, [messages, hasUserSentAMessage]);

  const handleAnimationEnd = (id: string) => {
    animatingMessages.current = animatingMessages.current.filter(
      (msgId) => msgId !== id
    );
  };

  const lastUserMessageIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUserMessage) return i;
    }
    return -1;
  }, [messages]);

  const lastAssistantRoleIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const isBuiltStatus =
        messages[i].id.includes('completed') ||
        messages[i].id.includes('failed');
      if (
        messages[i].role === 'assistant' &&
        !isBuiltStatus &&
        !messages[i].previewData?.length
      )
        return i;
    }
    return -1;
  }, [messages]);

  return (
    <div
      ref={messageHistoryWrapperRef}
      className={`flex-1 overflow-y-auto ${hideScrollbar ? '' : 'kds-expanding-scrollbar'} pb-[20px] bg-background-white`}
      style={
        // in cases where users want to resize the chat window manually, we need to hide the scrollbar styles to prevent layout shift
        hideScrollbar
          ? {
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }
          : undefined
      }
    >
      {isLoadingNewSession &&
      !isMessageResponseLoading &&
      !hideLoadingSpinner ? (
        <Loader loading={true} className="new-chat-loader" />
      ) : null}

      <div className="mx-[5px] mt-[15px] mb-[10px] flex flex-col gap-[30px] relative">
        {!isLoadingNewSession &&
          messages?.map(
            (
              {
                id,
                messageContent,
                isUserMessage,
                isResponseLoading,
                isResponseRefining,
                header,
                streaming,
                showFeedbackIcons,
                awaitingFinal,
                buildCompleted,
                bubble,
                showAvatar,
                floatAbove,
                statusDetails,
                previewData,
              },
              index
            ) => {
              if (previewData && previewData.length > 0) {
                const isLastMessage = index === messages.length - 1;
                return (
                  <div key={id}>
                    <PreviewCards
                      items={previewData}
                      onSelect={onPreviewSelect}
                    />
                    {isLastMessage && (
                      <div ref={scrollBottomRef} className="h-px w-full" />
                    )}
                    {isLastMessage && (
                      <div ref={previewSpacerRef} aria-hidden="true" />
                    )}
                  </div>
                );
              }

              return (
                <Message
                  key={id}
                  id={id}
                  isLastAssistantRole={
                    lastAssistantRoleIndex === index && index !== 0
                  }
                  isUserMessage={isUserMessage}
                  messageContent={messageContent}
                  header={header}
                  isResponseLoading={isResponseLoading}
                  isResponseRefining={isResponseRefining}
                  avatarAlt={t('Kizen AI')}
                  messageRef={
                    lastUserMessageIndex === index
                      ? lastUserMessageRef
                      : lastAssistantRoleIndex === index
                        ? lastAssistantMessageRef
                        : undefined
                  }
                  isAnimating={
                    animatingMessages.current.includes(id) &&
                    hasUserSentAMessage &&
                    streaming !== false
                  }
                  awaitingFinal={awaitingFinal}
                  buildCompleted={buildCompleted}
                  onAnimationEnd={handleAnimationEnd}
                  streaming={streaming}
                  isLastMessage={index === messages.length - 1 && index !== 0}
                  bottomRef={scrollBottomRef}
                  containerHeight={scrollToHeight}
                  showFixedHeight={showFixedHeight}
                  showFeedbackIcons={showFeedbackIcons}
                  disableMessageAnimation={disableMessageAnimation}
                  onCopyMessage={onCopyMessage}
                  onLikeMessage={onLikeMessage}
                  onDislikeMessage={onDislikeMessage}
                  onRefreshMessage={onRefreshMessage}
                  hideRefresh={hideRefresh}
                  renderMarkdown={renderMarkdown}
                  bubble={bubble}
                  showAvatar={showAvatar}
                  floatAbove={
                    floatAbove && index > 1 && index < messages.length - 1
                  }
                  statusDetails={statusDetails}
                />
              );
            }
          )}
      </div>
    </div>
  );
};
